import {ScheduleStateModel} from '@store/schedule/schedule-state.model';
import {Action, State, StateContext, Store} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {itemsPerPage} from '@shared/helpers';
import {
    ChangeCurrentScheduledDevicesPage,
    ChangeCurrentScheduledGroupsPage,
    CleanScheduleToCreateData, CleanSearchParamSchedule,
    CreateEvent,
    DeleteEventAreaDevice,
    DeleteEventAreaGroup,
    DeviceToScheduleCreate,
    FetchScheduledDevices,
    FetchScheduledGroups,
    GetScheduledDevices,
    GetScheduledGroups, GetSelectedDeviceScheduleById, GetSelectedGroupScheduleById,
    GroupToScheduleCreate,
    MediaToScheduleCreate,
    PlaylistToScheduleCreate,
    SearchScheduledDevices,
    SearchScheduledGroups,
    SetActiveSchedule,
    SetActiveTab,
    SetCurrentScheduledDevicesPage,
    SetCurrentScheduledGroupsPage,
    SetTimeToNewSchedule, TurnOffEditMode, TurnOnEditMode,
} from '@store/schedule/schedule.actions';
import {Observable} from 'rxjs';
import {GroupService} from '@services/group/group.service';
import {IResponse} from '@interfaces/response';
import {IGroup} from '@interfaces/group';
import {tap} from 'rxjs/operators';
import {DeviceService} from '@services/device/device.service';
import {IDevice, IDeviceArea} from '@interfaces/devices';
import {Navigate} from '@ngxs/router-plugin';
import {produce} from 'immer';
import {ScheduleService} from '@services/schedule/schedule.service';
import {ToggleScheduledDevicesLoader, ToggleScheduledGroupsLoader} from '@store/loader';
import {RouterSelectors} from '@store/router/router.selectors';


@State<ScheduleStateModel>({
    name: 'scheduleState',
    defaults: {
        groups: [],
        devices: [],
        totalGroups: 0,
        totalDevices: 0,
        itemsPerPage: itemsPerPage[5],
        groupsCurrentPage: 1,
        devicesCurrentPage: 1,
        searchParam: '',
        activeTab: 'group',
        activeSchedule: null,
        editMode: false,
        scheduleToCreate: {
            deviceGroupName: '',
            description: '',
            mainData: {
                area: 4,
                device: null,
                endLocal: '',
                endLocalTimestamp: '',
                group: null,
                medias: [],
                playlist: null,
                playlistName: null,
                repetition: null,
                scheduleType: null,
                startLocal: '',
                template: 2,
            }
        }
    }
})

@Injectable()
export class ScheduleState {
    constructor(private groupService: GroupService,
                private deviceService: DeviceService,
                private scheduleService: ScheduleService,
                private store: Store) {
    }

    @Action(CreateEvent)
    createEvent(ctx: StateContext<ScheduleStateModel>): Observable<IDeviceArea[]> {
        // No need name if we have playlistId
        const event = produce(ctx.getState().scheduleToCreate.mainData, draft => {
            if (draft.playlist) {
                draft.playlistName = null;
            }
        });
        return this.scheduleService.createEvent(event).pipe(
            tap((createdSchedule) => {
                ctx.dispatch([
                    new FetchScheduledDevices(),
                    new FetchScheduledGroups()
                ]);

                if (createdSchedule[0].deviceId) {
                    ctx.patchState({activeTab: 'device'});
                    ctx.dispatch(new Navigate([`app/schedule/schedule-device/`, createdSchedule[0].deviceId], {}, {queryParamsHandling: 'merge'}));
                }
                if (createdSchedule[0].groupId) {
                    ctx.patchState({activeTab: 'group'});
                    ctx.dispatch(new Navigate([`app/schedule/schedule-group/`, createdSchedule[0].groupId], {}, { queryParamsHandling: 'merge' }));
                }
                ctx.patchState({activeSchedule: createdSchedule});
            })
        );
    }

    @Action(FetchScheduledGroups, {cancelUncompleted: true})
    fetchScheduledGroups(ctx: StateContext<ScheduleStateModel>, {params}: FetchScheduledGroups): Observable<any> {
        const limit = ctx.getState().itemsPerPage;
        const skip = limit * (ctx.getState().groupsCurrentPage - 1);
        const search = ctx.getState().searchParam;
        const onSchedule = 1;
        this.store.dispatch(new ToggleScheduledGroupsLoader(true));
        return this.groupService.getGroups({
            limit,
            skip,
            search,
            onSchedule,
            ...params
        })
            .pipe(
                tap((res: IResponse<IGroup>) => {
                    const state = ctx.getState();
                    const activeDeviceId = +this.store.selectSnapshot(RouterSelectors.params)?.groupId || null;
                    // update active schedule if user create new one with the same device
                    const updatedActiveSchedule = res.rows.find((d) => d.group_id === activeDeviceId);
                    const deviceAreas = updatedActiveSchedule ? updatedActiveSchedule.areas : null;
                    ctx.setState({
                        ...state, groups: res.rows, totalGroups: res.total,
                        activeSchedule: deviceAreas || state.activeSchedule
                    });
                    this.store.dispatch(new ToggleScheduledGroupsLoader(false));
                })
            );
    }

    @Action(FetchScheduledDevices, {cancelUncompleted: true})
    fetchScheduledDevices(ctx: StateContext<ScheduleStateModel>, {params}: FetchScheduledDevices): Observable<any> {
        const limit = ctx.getState().itemsPerPage;
        const skip = limit * (ctx.getState().devicesCurrentPage - 1);
        const search = ctx.getState().searchParam;
        const onSchedule = 1;
        const noGroup = 1;
        this.store.dispatch(new ToggleScheduledDevicesLoader(true));
        return this.deviceService.getDevices({
            limit,
            skip,
            search,
            onSchedule,
            noGroup,
            ...params
        })
            .pipe(
                tap((devices: IResponse<IDevice>) => {
                    const state = ctx.getState();
                    const activeDeviceId = +this.store.selectSnapshot(RouterSelectors.params)?.deviceId || null;
                    // update active schedule if user create new one with the same device
                    const updatedActiveSchedule = devices.rows.find((d) => d.id === activeDeviceId);
                    const deviceAreas = updatedActiveSchedule ? updatedActiveSchedule.areas : null;
                    ctx.setState({
                        ...state, devices: devices.rows,
                            totalDevices: devices.total,
                        activeSchedule: deviceAreas || state.activeSchedule
                        });
                    this.store.dispatch(new ToggleScheduledDevicesLoader(false));
                }),
            );
    }

    @Action(GetScheduledGroups, {cancelUncompleted: true})
    getScheduledGroups(ctx: StateContext<ScheduleStateModel>): Observable<any> {
        const state = ctx.getState();
        ctx.setState({...state, groupsCurrentPage: 1, searchParam: ''});
        return ctx.dispatch(new FetchScheduledGroups());
    }

    @Action(GetScheduledDevices, {cancelUncompleted: true})
    getScheduledDevices(ctx: StateContext<ScheduleStateModel>): Observable<any> {
        const state = ctx.getState();
        ctx.setState({...state, devicesCurrentPage: 1, searchParam: ''});
        return ctx.dispatch(new FetchScheduledDevices());
    }

    @Action(SetCurrentScheduledDevicesPage)
    setCurrentDevicesPage(ctx: StateContext<ScheduleStateModel>, {page}: ChangeCurrentScheduledDevicesPage): Observable<void> {
        ctx.patchState({devicesCurrentPage: page});

        return ctx.dispatch(
            new Navigate([], {devicesPage: page}, {queryParamsHandling: 'merge'}),
        );
    }

    @Action(ChangeCurrentScheduledDevicesPage)
    changeCurrentScheduledDevicesPage(ctx: StateContext<ScheduleStateModel>, {page}: ChangeCurrentScheduledDevicesPage): Observable<any> {
        this.setCurrentDevicesPage(ctx, {page});

        return ctx.dispatch(new FetchScheduledDevices());
    }

    @Action(SetCurrentScheduledGroupsPage)
    setCurrentGroupsPage(ctx: StateContext<ScheduleStateModel>, {page}: ChangeCurrentScheduledGroupsPage): Observable<void> {
        ctx.patchState({groupsCurrentPage: page});

        return ctx.dispatch(
            new Navigate([], {groupsPage: page}, {queryParamsHandling: 'merge'}),
        );
    }

    @Action(ChangeCurrentScheduledGroupsPage)
    changeCurrentScheduledGroupsPage(ctx: StateContext<ScheduleStateModel>, {page}: ChangeCurrentScheduledGroupsPage): Observable<any> {
        this.setCurrentGroupsPage(ctx, {page});

        return ctx.dispatch(new FetchScheduledGroups());
    }

    @Action(SearchScheduledDevices)
    searchScheduledDevices(ctx: StateContext<ScheduleStateModel>, {searchParam}: SearchScheduledDevices): Observable<any> {
        const state = ctx.getState();
        ctx.setState({...state, searchParam, devicesCurrentPage: 1});
        const navigate = searchParam ?
            new Navigate([], {devicesSearch: searchParam, devicesPage: 1}, {queryParamsHandling: 'merge'}) :
            new Navigate([], {devicesSearch: null, devicesPage: 1}, {queryParamsHandling: 'merge'});

        return ctx.dispatch([
            navigate,
            new FetchScheduledDevices()
        ]);
    }

    @Action(SearchScheduledGroups)
    searchScheduledGroups(ctx: StateContext<ScheduleStateModel>, {searchParam}: SearchScheduledGroups): Observable<any> {
        const state = ctx.getState();
        ctx.setState({...state, searchParam, groupsCurrentPage: 1});
        const navigate = searchParam ?
            new Navigate([], {groupsSearch: searchParam, groupsPage: 1}, {queryParamsHandling: 'merge'}) :
            new Navigate([], {groupsSearch: null, groupsPage: 1}, {queryParamsHandling: 'merge'});


        return ctx.dispatch([
            navigate,
            new FetchScheduledGroups()
        ]);
    }

    @Action(SetActiveTab)
    setActiveTab(ctx: StateContext<ScheduleStateModel>, {tab}: SetActiveTab): Observable<any> {
        const state = ctx.getState();
        ctx.setState({...state, activeTab: tab, devicesCurrentPage: 1, groupsCurrentPage: 1});
        const navigate = new Navigate([], {tab}, {queryParamsHandling: 'merge'});

        if (tab === 'device') ctx.dispatch(new FetchScheduledDevices());
        else ctx.dispatch(new FetchScheduledGroups());

        return ctx.dispatch([
            navigate,
        ]);
    }

    @Action(GroupToScheduleCreate)
    groupToScheduleCreate(ctx: StateContext<ScheduleStateModel>, {group, name}: GroupToScheduleCreate): void {
        const state = produce(ctx.getState(), draft => {
            draft.scheduleToCreate.mainData.group = group;
            draft.scheduleToCreate.deviceGroupName = name;
            draft.scheduleToCreate.mainData.device = null;
        });

        ctx.setState(state);
    }

    @Action(DeviceToScheduleCreate)
    deviceToScheduleCreate(ctx: StateContext<ScheduleStateModel>, {device, name}: DeviceToScheduleCreate): void {
        const state = produce(ctx.getState(), draft => {
            draft.scheduleToCreate.mainData.device = device;
            draft.scheduleToCreate.deviceGroupName = name;
            draft.scheduleToCreate.mainData.group = null;
        });

        ctx.setState(state);
    }

    @Action(PlaylistToScheduleCreate)
    playlistToScheduleCreate(ctx: StateContext<ScheduleStateModel>, {
        playlist,
        description,
        name
    }: PlaylistToScheduleCreate): void {
        const state = produce(ctx.getState(), draft => {
            draft.scheduleToCreate.mainData.playlist = playlist;
            draft.scheduleToCreate.description = description;
            draft.scheduleToCreate.mainData.medias = [];
            draft.scheduleToCreate.mainData.playlistName = name;
        });

        ctx.setState(state);
    }

    @Action(MediaToScheduleCreate)
    mediaToScheduleCreate(ctx: StateContext<ScheduleStateModel>, {media, playlistName}: MediaToScheduleCreate): void {
        const state = produce(ctx.getState(), draft => {
            draft.scheduleToCreate.mainData.medias = media;
            draft.scheduleToCreate.mainData.playlistName = playlistName;
            draft.scheduleToCreate.mainData.playlist = null;
        });

        ctx.setState(state);
    }

    @Action(SetActiveSchedule)
    setActiveSchedule(ctx: StateContext<ScheduleStateModel>, {schedule}: SetActiveSchedule): void {
        ctx.patchState({activeSchedule: schedule});
    }

    @Action(SetTimeToNewSchedule)
    setTimeToNewSchedule(ctx: StateContext<ScheduleStateModel>, {payload}: SetTimeToNewSchedule): void {
        const state = produce(ctx.getState(), draft => {
            draft.scheduleToCreate.mainData.startLocal = payload.startLocal;
            draft.scheduleToCreate.mainData.endLocal = payload.endLocal;
            draft.scheduleToCreate.mainData.endLocalTimestamp = payload.endLocalTimeStamp;
            draft.scheduleToCreate.mainData.scheduleType = payload.scheduleType;
            draft.scheduleToCreate.mainData.repetition = payload.repetition;
        });

        ctx.setState(state);
    }

    @Action(CleanScheduleToCreateData)
    cleanScheduleToCreateData(ctx: StateContext<ScheduleStateModel>): void {
        const state = produce(ctx.getState(), draft => {
            draft.scheduleToCreate.deviceGroupName = '';
            draft.scheduleToCreate.description = '';
            draft.scheduleToCreate.mainData.playlistName = null;
            draft.scheduleToCreate.mainData.device = null;
            draft.scheduleToCreate.mainData.endLocal = '';
            draft.scheduleToCreate.mainData.endLocalTimestamp = '';
            draft.scheduleToCreate.mainData.group = null;
            draft.scheduleToCreate.mainData.medias = [];
            draft.scheduleToCreate.mainData.playlist = null;
            draft.scheduleToCreate.mainData.repetition = null;
            draft.scheduleToCreate.mainData.scheduleType = null;
            draft.scheduleToCreate.mainData.startLocal = '';
        });

        ctx.setState(state);
    }

    @Action(DeleteEventAreaDevice)
    deleteEventAreaDevice(ctx: StateContext<ScheduleStateModel>, {id}: DeleteEventAreaDevice): Observable<void> {
        return this.scheduleService.deleteEventDevice(id)
            .pipe(
                tap(() => {
                    const state = ctx.getState();
                    const deletedDeviceCount = state.activeSchedule?.length === 1 ? 1 : 0;
                    const devicesCurrentPage = this.getCurrentPageAfterDelete(
                        state.devicesCurrentPage,
                        state.itemsPerPage,
                        state.totalDevices,
                        deletedDeviceCount
                    );

                    ctx.patchState({
                        devicesCurrentPage,
                        activeSchedule: state.activeSchedule?.filter(event => event.id !== id)
                    });
                    ctx.dispatch(new Navigate([], {devicesPage: devicesCurrentPage}, {queryParamsHandling: 'merge'}));
                    ctx.dispatch(new FetchScheduledDevices());
                })
            );
    }

    @Action(DeleteEventAreaGroup)
    deleteEventAreaGroup(ctx: StateContext<ScheduleStateModel>, {id}: DeleteEventAreaGroup): any {
        return this.scheduleService.deleteEventGroup(id)
            .pipe(
                tap(() => {
                    const state = ctx.getState();
                    const deletedGroupCount = state.activeSchedule?.length === 1 ? 1 : 0;
                    const groupsCurrentPage = this.getCurrentPageAfterDelete(
                        state.groupsCurrentPage,
                        state.itemsPerPage,
                        state.totalGroups,
                        deletedGroupCount
                    );

                    ctx.patchState({
                        groupsCurrentPage,
                        activeSchedule: state.activeSchedule?.filter(event => event.id !== id)
                    });
                    ctx.dispatch(new Navigate([], {groupsPage: groupsCurrentPage}, {queryParamsHandling: 'merge'}));
                    ctx.dispatch(new FetchScheduledGroups());
                })
            );
    }

    @Action(CleanSearchParamSchedule)
    cleanSearchParamSchedule(ctx: StateContext<ScheduleStateModel>): Observable<void> {
        ctx.patchState({searchParam: '', devicesCurrentPage: 1, groupsCurrentPage: 1});
        const navigate = new Navigate([], {
            devicesSearch: null,
            groupsSearch: null,
            tab: ctx.getState().activeTab
        }, {queryParamsHandling: 'merge'});
        return ctx.dispatch(navigate);
    }

    @Action(GetSelectedDeviceScheduleById)
    getSelectedDeviceScheduleById(ctx: StateContext<ScheduleStateModel>, {id}: GetSelectedDeviceScheduleById): void {
        const state = ctx.getState();
        ctx.patchState({
            activeSchedule: [...state.devices.filter(device => device.id === id).map(device => device.areas)][0]
        });
    }

    @Action(GetSelectedGroupScheduleById)
    getSelectedGroupScheduleById(ctx: StateContext<ScheduleStateModel>, {id}: GetSelectedGroupScheduleById): void {
        const state = ctx.getState();

        ctx.patchState({
            activeSchedule: state.groups.filter(group => group.group_id === id).map(group => group.areas)[0]
        });
    }

    @Action(TurnOnEditMode)
    turnOnEditMode(ctx: StateContext<ScheduleStateModel>): void {
        ctx.patchState({
            editMode: true
        });
    }

    @Action(TurnOffEditMode)
    turnOffEditMode(ctx: StateContext<ScheduleStateModel>): void {
        ctx.patchState({
            editMode: false
        });
    }

    private getCurrentPageAfterDelete(currentPage: number, perPage: number, total = 0, deletedCount = 1): number {
        const totalAfterDelete = Math.max(total - deletedCount, 0);
        const lastAvailablePage = Math.max(Math.ceil(totalAfterDelete / perPage), 1);

        return Math.min(currentPage, lastAvailablePage);
    }
}
